import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch, RootState } from '../../store';
import apis from '../../utils/apis';
import { formatRoleNameForDisplay } from '../../utils/roleFormatter';
import DetailFrame from '../shared/Frames/DetailFrame';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import Tabs from '../shared/TailwindComponents/Tabs';
import ProfileImage from '../Header/ProfileImage';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import {
  assignUserToAddon,
  revokeUserFromAddon,
  fetchFeatureRequests,
  approveFeatureRequest,
  denyFeatureRequest,
  FeatureRequestItem,
} from '../../store/actions/PlanActions';
import * as PiIcons from 'react-icons/pi';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import { formatDateTime } from '../../utils/functions';

const sectionIconMap: Record<string, string> = {
  'Dashboard': 'PiDashboard',
  'Tasks': 'PiCheckSquare',
  'Calendar': 'PiCalendar',
  'Messages': 'PiChat',
  'Clients': 'PiUsers',
  'Lawyers': 'PiUserCircle',
  'Projects': 'PiTreeStructure',
  'Matters': 'PiBriefcase',
  'Files': 'PiFiles',
  'Research': 'PiMagnifyingGlass',
  'Tables': 'PiGridFour',
  'Analytics': 'PiChartBar',
  'Data Sources': 'PiDatabase',
  'Finder': 'PiUserSwitch',
  'Templates': 'PiFileText',
  'People': 'PiUsers',
  'Departments': 'PiBuilding',
  'Teams': 'PiUsersThree',
  'Finance': 'PiCoinVertical',
};

const capitalizeSection = (name: string): string => {
  // Handle underscores like "data_sources" -> "Data Sources"
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getIconByName = (name: string) => {
  const normalizedName = capitalizeSection(name);
  const iconKey = sectionIconMap[normalizedName] || sectionIconMap[name] || 'PiGear';
  const IconComponent = (PiIcons as any)[iconKey + 'Light'] || (PiIcons as any)[iconKey];
  return IconComponent ? <IconComponent className="h-4 w-4" /> : null;
};

// Permission sections hidden from the UI for now
const HIDDEN_PERMISSION_SECTIONS = [
  'client_pipeline', 'review', 'finance', 'messages', 'dashboard', 'analytics',
];

/* ─── License types ──────────────────────────────────────────── */

interface OrgLicense {
  org_license_id: number;
  license_id: number;
  name: string;
  display_name: string;
  type: string;
  description: string | null;
  feature_gates: { sections?: string[]; ai_features?: string[]; limits?: Record<string, any> };
  seat_count: number;
  quantity: number;
  status: string;
  is_per_seat: boolean;
  price_monthly: number | null;
  price_annual: number | null;
  license_starts_at: string | null;
  license_expires_at: string | null;
  stripe_subscription_id: string | null;
  assigned_to_user: boolean;
  assigned_count: number;
}

interface AvailableAddon {
  license_id: number;
  org_license_id?: number;
  name: string;
  display_name: string | null;
  type: string;
  description: string | null;
  feature_gates: Record<string, any>;
  is_per_seat: boolean;
  price_monthly: number | null;
  price_annual: number | null;
  source: 'org' | 'catalog';
  seat_count?: number;
  quantity?: number;
  assigned_count?: number;
}

interface UpgradeOption {
  license_id: number;
  name: string;
  display_name: string;
  description: string | null;
  price_monthly: number | null;
  price_annual: number | null;
  feature_gates: Record<string, any>;
}

interface UserLicenseData {
  base_plan: OrgLicense | null;
  org_base_plan: OrgLicense | null;
  has_user_plan_override: boolean;
  has_base_seat: boolean;
  org_licenses: OrgLicense[];
  user_active_addons: OrgLicense[];
  available_addons: AvailableAddon[];
  user_addon_ids: number[];
  is_org_admin: boolean;
  is_full_plan: boolean;
  upgrade_options: UpgradeOption[];
  downgrade_options: UpgradeOption[];
  pending_requests_count: number;
}

const TYPE_BADGE_COLOR: Record<string, BadgeColor> = {
  base: 'blue',
  trial: 'yellow',
  pilot: 'purple',
  addon: 'gray',
};

/* ─── UserLicensePanel ───────────────────────────────────────── */

const FULL_PLAN_ADDON_THRESHOLD = 3;

type LicenseTab = 'plan' | 'addons' | 'requests';

const UserLicensePanel: React.FC<{ userId: number }> = ({ userId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [data, setData] = useState<UserLicenseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [requests, setRequests] = useState<FeatureRequestItem[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestActionLoading, setRequestActionLoading] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<LicenseTab>('plan');

  const loadLicenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apis.get(`/admin/users/${userId}/licenses`);
      if (res.data.success) {
        setData(res.data.data);
      } else {
        setError(res.data.error || 'Failed to load licenses');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load licenses');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const result = await dispatch(fetchFeatureRequests()) as unknown as FeatureRequestItem[] | null;
      if (result) {
        setRequests(result.filter(r => r.requested_by?.id === userId));
      }
    } catch { /* handled by action */ }
    finally { setRequestsLoading(false); }
  }, [dispatch, userId]);

  useEffect(() => { loadLicenses(); }, [loadLicenses]);
  useEffect(() => { loadRequests(); }, [loadRequests]);

  /* ── Handler: Assign a base-plan seat (license) to the user ── */
  const handleAssignSeat = async (orgLicenseId: number) => {
    const plan = data?.org_licenses.find(l => l.org_license_id === orgLicenseId);
    const planName = plan?.display_name || plan?.name || 'this plan';
    const isStarter = plan?.name === 'pons_starter';

    setActionLoading(orgLicenseId);
    try {
      await dispatch(assignUserToAddon({ org_license_id: orgLicenseId, user_id: userId }));
      await loadLicenses();

      if (isStarter) {
        // Switch to add-ons tab so admin can immediately attach add-ons
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('License assigned — you can now add optional add-ons for this user.'),
          modalType: 'success',
          duration: 4000,
        }));
        setActiveTab('addons');
      } else {
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('License assigned — user is now on {{plan}}.', { plan: planName }),
          modalType: 'success',
          duration: 3000,
        }));
      }
    } catch {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to assign license'),
        modalType: 'error',
        duration: 4000,
      }));
    } finally {
      setActionLoading(null);
    }
  };

  /* ── Handler: Assign org-owned addon ── */
  const handleAssign = (orgLicenseId: number) => {
    const addon = data?.available_addons.find(a => a.source === 'org' && a.org_license_id === orgLicenseId)
      || data?.user_active_addons.find(a => a.org_license_id === orgLicenseId);
    const addonName = addon?.display_name || addon?.name || 'Add-on';
    const priceInfo = addon?.price_monthly != null ? `€${addon.price_monthly}/${t('mo')}` : '';

    let subMsg = priceInfo
      ? t('This will grant the user access to {{addon}} ({{price}}).', { addon: addonName, price: priceInfo })
      : t('This will grant the user access to {{addon}}.', { addon: addonName });

    if (activeAddonCount >= FULL_PLAN_ADDON_THRESHOLD && fullPlanOption) {
      subMsg += `<br/><br/><strong>${t('Tip')}:</strong> ${t('This user already has {{count}} add-ons. Consider upgrading to {{plan}} (€{{price}}/mo) which includes everything.', { count: activeAddonCount, plan: fullPlanOption.display_name, price: fullPlanOption.price_monthly })}`;
    }

    dispatch(showModal({
      type: 'confirmation',
      message: t('Assign {{addon}}?', { addon: addonName }),
      subMsg,
      showModal: true,
      onConfirm: async () => {
        dispatch(hideModal());
        setActionLoading(orgLicenseId);
        try {
          await dispatch(assignUserToAddon({ org_license_id: orgLicenseId, user_id: userId }));
          dispatch(showFeedbackModal({ showModal: true, message: t('Add-on assigned'), modalType: 'success', duration: 3000 }));
          await loadLicenses();
        } catch {
          dispatch(showFeedbackModal({ showModal: true, message: t('Failed to assign add-on'), modalType: 'error', duration: 4000 }));
        } finally {
          setActionLoading(null);
        }
      },
      onCancel: () => dispatch(hideModal()),
    }));
  };

  /* ── Handler: Assign catalog addon ── */
  const handleAssignCatalog = (licenseId: number) => {
    const addon = data?.available_addons.find(a => a.source === 'catalog' && a.license_id === licenseId);
    const addonName = addon?.display_name || addon?.name || 'Add-on';
    const priceInfo = addon?.price_monthly != null ? `€${addon.price_monthly}/${t('mo')}` : '';

    let subMsg = priceInfo
      ? t('This add-on is not yet purchased. Assigning will grant the user access to {{addon}} ({{price}}).', { addon: addonName, price: priceInfo })
      : t('This add-on is not yet purchased. Assigning will grant the user access to {{addon}}.', { addon: addonName });

    if (activeAddonCount >= FULL_PLAN_ADDON_THRESHOLD && fullPlanOption) {
      subMsg += `<br/><br/><strong>${t('Tip')}:</strong> ${t('This user already has {{count}} add-ons. Consider upgrading to {{plan}} (€{{price}}/mo) which includes everything.', { count: activeAddonCount, plan: fullPlanOption.display_name, price: fullPlanOption.price_monthly })}`;
    }

    dispatch(showModal({
      type: 'confirmation',
      message: t('Assign {{addon}}?', { addon: addonName }),
      subMsg,
      showModal: true,
      onConfirm: async () => {
        dispatch(hideModal());
        setActionLoading(licenseId);
        try {
          await apis.post(`/admin/users/${userId}/assign-addon`, { license_id: licenseId });
          dispatch(showFeedbackModal({ showModal: true, message: t('Add-on assigned'), modalType: 'success', duration: 3000 }));
          await loadLicenses();
        } catch {
          dispatch(showFeedbackModal({ showModal: true, message: t('Failed to assign add-on'), modalType: 'error', duration: 4000 }));
        } finally {
          setActionLoading(null);
        }
      },
      onCancel: () => dispatch(hideModal()),
    }));
  };

  const handleRevoke = async (orgLicenseId: number) => {
    // Determine if this is a base-plan seat or an add-on for correct messaging
    const lic = data?.org_licenses.find(l => l.org_license_id === orgLicenseId);
    const isBase = lic && ['base', 'trial', 'pilot'].includes(lic.type);
    const label = isBase ? t('License') : t('Add-on');

    setActionLoading(orgLicenseId);
    try {
      await dispatch(revokeUserFromAddon({ org_license_id: orgLicenseId, user_id: userId }));
      dispatch(showFeedbackModal({
        showModal: true,
        message: isBase ? t('License revoked — user no longer has access.') : t('Add-on revoked'),
        modalType: 'success',
        duration: 3000,
      }));
      await loadLicenses();
    } catch {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to revoke {{label}}', { label: label.toLowerCase() }),
        modalType: 'error',
        duration: 4000,
      }));
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveRequest = async (requestId: number) => {
    setRequestActionLoading(requestId);
    try {
      const ok = await dispatch(approveFeatureRequest(requestId)) as unknown as boolean;
      if (ok) {
        await loadRequests();
        await loadLicenses();
      }
    } finally {
      setRequestActionLoading(null);
    }
  };

  const handleDenyRequest = async (requestId: number) => {
    setRequestActionLoading(requestId);
    try {
      const ok = await dispatch(denyFeatureRequest(requestId)) as unknown as boolean;
      if (ok) await loadRequests();
    } finally {
      setRequestActionLoading(null);
    }
  };

  /* ── Handler: Assign plan to THIS user (per-user, does NOT affect org) ── */
  const handleChangePlan = (licenseId: number, direction: 'upgrade' | 'downgrade') => {
    const allPlans = [...(data?.upgrade_options || []), ...(data?.downgrade_options || [])];
    const plan = allPlans.find(o => o.license_id === licenseId);
    const planName = plan?.display_name || plan?.name || 'this plan';
    const isFull = plan?.name === 'pons_full';
    const addonCount = data?.user_active_addons.length ?? 0;

    let subMsg = direction === 'upgrade'
      ? t('This will assign <b>{{plan}}</b> to this user only. Other users in the organization are not affected.', { plan: planName })
      : t('This will change this user\'s plan to <b>{{plan}}</b>. Other users in the organization are not affected.', { plan: planName });
    if (isFull && addonCount > 0) {
      subMsg += `<br/><br/>⚠️ <b>${addonCount} active add-on(s)</b> will be removed — Full plan includes everything.`;
    }

    dispatch(showModal({
      type: 'confirmation',
      message: direction === 'upgrade'
        ? t('Assign {{plan}} to this user?', { plan: planName })
        : t('Change to {{plan}}?', { plan: planName }),
      subMsg,
      showModal: true,
      onConfirm: async () => {
        dispatch(hideModal());
        setActionLoading(licenseId);
        try {
          await apis.post(`/admin/users/${userId}/assign-plan`, { license_id: licenseId });
          dispatch(showFeedbackModal({ showModal: true, message: t('Plan assigned to user'), modalType: 'success', duration: 3000 }));
          await loadLicenses();
        } catch {
          dispatch(showFeedbackModal({ showModal: true, message: t('Failed to assign plan'), modalType: 'error', duration: 4000 }));
        } finally {
          setActionLoading(null);
        }
      },
      onCancel: () => dispatch(hideModal()),
    }));
  };

  const fullPlanOption = useMemo(() => {
    if (!data) return null;
    return data.upgrade_options.find(o => o.name === 'pons_full') || null;
  }, [data]);

  const activeAddonCount = data?.user_active_addons.length ?? 0;
  const isFullPlan = data?.is_full_plan ?? false;
  // Only PONS Starter supports add-ons. Hide addon tab for Pilot, Trail, Full.
  const orgPlanName = data?.org_base_plan?.name ?? data?.base_plan?.name ?? '';
  const isAddonEligible = orgPlanName === 'pons_starter';
  const showUpsell = !isFullPlan && activeAddonCount >= FULL_PLAN_ADDON_THRESHOLD && fullPlanOption !== null;
  const pendingRequests = requests.filter(r => r.status === 'pending');

  // Total monthly cost for this user (base + addons)
  const totalMonthlyCost = useMemo(() => {
    if (!data) return 0;
    let total = data.base_plan?.price_monthly ?? 0;
    for (const addon of data.user_active_addons || []) {
      total += addon.price_monthly ?? 0;
    }
    return total;
  }, [data]);

  if (loading) {
    return (
      <div className="space-y-4 p-2 animate-fadePulse">
        <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>;
  }

  if (!data || data.org_licenses.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-dashed border-gray-200 dark:border-gray-700 text-center">
        <p className="text-sm text-gray-600 dark:text-gray-400">{t('No active licenses for this organization.')}</p>
      </div>
    );
  }

  const { base_plan, org_base_plan, has_user_plan_override, has_base_seat, is_org_admin, user_active_addons, available_addons, upgrade_options, downgrade_options = [], pending_requests_count } = data;

  /* ── Sub-tab definitions ── */
  const tabs: { key: LicenseTab; label: string; count?: number; hidden?: boolean }[] = [
    { key: 'plan', label: t('Plan') },
    { key: 'addons', label: t('Add-ons'), count: activeAddonCount, hidden: !isAddonEligible && activeAddonCount === 0 },
    { key: 'requests', label: t('Requests'), count: pendingRequests.length },
  ];
  const visibleTabs = tabs.filter(tab => !tab.hidden);

  const tabItems = visibleTabs.map(tab => ({ label: tab.label, value: tab.key }));
  const tabBadges: Record<string, number> = {};
  for (const tab of visibleTabs) {
    if (tab.count != null && tab.count > 0) tabBadges[tab.key] = tab.count;
  }

  return (
    <div className="flex gap-4 pt-4 pr-4">
      {/* ── Vertical nav ── */}
      <div className="w-32 flex-shrink-0">
        <Tabs
          tabs={tabItems}
          activeTab={activeTab}
          onTabClick={(val) => setActiveTab(val as LicenseTab)}
          vertical
          compact
          badgeCounts={tabBadges}
        />
      </div>

      {/* ── Content ── */}
      <div className="flex-1 min-w-0">

      {/* ═══════ PLAN TAB ═══════ */}
      {activeTab === 'plan' && (
        <div className="space-y-4">

          {/* ── UNSEATED USER: no license assigned ── */}
          {!has_base_seat && !is_org_admin && (() => {
            // Only show purchasable base plans (not trials/pilots — those are internal)
            const ownedBasePlans = (data?.org_licenses ?? [])
              .filter((l) => l.type === 'base')
              .reduce<OrgLicense[]>((acc, lic) => {
                if (!acc.find((a) => a.license_id === lic.license_id)) acc.push(lic);
                return acc;
              }, []);

            return (
              <>
                {/* Status banner */}
                <div className="rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10 p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/30 shrink-0">
                      <PiIcons.PiWarningLight className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{t('No license assigned')}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {t('This user does not have a license and cannot access the platform. Assign a license below to give them access.')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Assign license section */}
                <div className="space-y-3">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('Assign a license')}
                  </p>

                  {/* Org's current base plan(s) */}
                  {ownedBasePlans.map((plan) => (
                    <PlanOptionCard
                      key={plan.org_license_id}
                      label={plan.display_name || plan.name}
                      type={plan.type}
                      price={plan.price_monthly}
                      sections={plan.feature_gates?.sections}
                      isFull={false}
                      loading={actionLoading === plan.org_license_id}
                      disabled={actionLoading != null}
                      buttonLabel={t('Assign license')}
                      onClick={() => handleAssignSeat(plan.org_license_id)}
                      t={t}
                    />
                  ))}

                  {/* Higher-tier plans available as upgrade */}
                  {upgrade_options.map((plan) => (
                    <PlanOptionCard
                      key={plan.license_id}
                      label={plan.display_name || plan.name}
                      type="base"
                      price={plan.price_monthly}
                      sections={plan.feature_gates?.sections}
                      isFull={false}
                      loading={actionLoading === plan.license_id}
                      disabled={actionLoading != null}
                      buttonLabel={t('Upgrade & assign')}
                      highlight
                      onClick={() => handleChangePlan(plan.license_id, 'upgrade')}
                      t={t}
                    />
                  ))}

                  {/* Downgrade options */}
                  {downgrade_options.map((plan) => (
                    <PlanOptionCard
                      key={plan.license_id}
                      label={plan.display_name || plan.name}
                      type="base"
                      price={plan.price_monthly}
                      sections={plan.feature_gates?.sections}
                      isFull={false}
                      loading={actionLoading === plan.license_id}
                      disabled={actionLoading != null}
                      buttonLabel={t('Downgrade & assign')}
                      onClick={() => handleChangePlan(plan.license_id, 'downgrade')}
                      t={t}
                    />
                  ))}

                  {ownedBasePlans.length === 0 && upgrade_options.length === 0 && downgrade_options.length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 italic">{t('No plans available for this organization.')}</p>
                  )}
                </div>
              </>
            );
          })()}

          {/* ── SEATED USER (or admin): show their active license ── */}
          {(has_base_seat || is_org_admin) && (
            <>
              {/* License status banner */}
              <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-700 text-xs text-emerald-700 dark:text-emerald-300">
                <div className="flex items-center gap-2">
                  <PiIcons.PiSealCheckLight className="h-4 w-4 flex-shrink-0" />
                  <span>
                    {is_org_admin
                      ? t('Organization admin — always has platform access.')
                      : t('Active license — this user can access the platform.')}
                  </span>
                </div>
                {/* Revoke button — only for non-admins with explicit assignment */}
                {!is_org_admin && has_user_plan_override && base_plan && (
                  <button
                    onClick={() => handleRevoke(base_plan.org_license_id)}
                    disabled={actionLoading === base_plan.org_license_id}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 underline ml-3 shrink-0"
                  >
                    {t('Revoke license')}
                  </button>
                )}
              </div>

              {/* Active license card */}
              {base_plan && (
                <div className={`rounded-lg border p-4 ${
                  isFullPlan
                    ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-700'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      {isFullPlan
                        ? <PiIcons.PiCrownSimpleLight className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        : <PiIcons.PiShieldCheckLight className="h-4 w-4 text-gray-900 dark:text-gray-100" />
                      }
                      <div>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {base_plan.display_name || base_plan.name}
                        </span>
                        {isFullPlan && (
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                            {t('Full access to all features — no add-ons needed')}
                          </p>
                        )}
                      </div>
                      <Badge color={TYPE_BADGE_COLOR[base_plan.type] || 'gray'}>
                        {base_plan.type.charAt(0).toUpperCase() + base_plan.type.slice(1)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      {base_plan.price_monthly != null && (
                        <span className="font-medium">€{base_plan.price_monthly}/{t('mo')}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <PiIcons.PiUsersLight className="h-3.5 w-3.5" />
                        {base_plan.assigned_count} / {base_plan.seat_count >= 500 ? '∞' : base_plan.seat_count} {t('seats')}
                      </span>
                      {base_plan.license_expires_at && (
                        <span>
                          {t('Expires')} {formatDateTime(base_plan.license_expires_at)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Included sections */}
                  {base_plan.feature_gates?.sections && base_plan.feature_gates.sections.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {base_plan.feature_gates.sections.map((s: string) => (
                        <span key={s} className="inline-flex items-center gap-1 text-xs bg-white/60 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-600">
                          {getIconByName(s)}
                          {capitalizeSection(s)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Monthly cost summary */}
              {totalMonthlyCost > 0 && (
                <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 text-xs">
                  <span className="text-gray-500 dark:text-gray-400">{t('Total per month for this user')}</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{'\u20AC'}{totalMonthlyCost.toFixed(2)}/{t('mo')}</span>
                </div>
              )}

              {/* Per-user plan override notice */}
              {has_user_plan_override && org_base_plan && base_plan && org_base_plan.license_id !== base_plan.license_id && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-200 dark:border-indigo-700 text-xs text-indigo-700 dark:text-indigo-300">
                  <PiIcons.PiUserSwitchLight className="h-4 w-4 flex-shrink-0" />
                  <span>
                    {t('This user has a per-user plan override.')} {t('Org default:')} <b>{org_base_plan.display_name || org_base_plan.name}</b>
                  </span>
                </div>
              )}

              {/* Upsell to PONS Full */}
              {showUpsell && fullPlanOption && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <PiIcons.PiSparkleLight className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {t('Consider upgrading to')} {fullPlanOption.display_name}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                          {t('This user has {{count}} add-ons.', { count: activeAddonCount })} {fullPlanOption.display_name} {t('includes everything for')} €{fullPlanOption.price_monthly}/{t('mo')}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="primary"
                      size="small"
                      onClick={() => handleChangePlan(fullPlanOption.license_id, 'upgrade')}
                      disabled={actionLoading === fullPlanOption.license_id}
                      isLoading={actionLoading === fullPlanOption.license_id}
                    >
                      {t('Upgrade')}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Upgrade options — only shown when user already has a license */}
          {has_base_seat && upgrade_options.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{t('Upgrade license')}</p>
              <div className="flex flex-wrap gap-2">
                {upgrade_options.map((plan) => (
                  <button
                    key={plan.license_id}
                    onClick={() => handleChangePlan(plan.license_id, 'upgrade')}
                    disabled={actionLoading === plan.license_id}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50"
                  >
                    <PiIcons.PiArrowUpLight className="h-3 w-3" />
                    {plan.display_name}
                    {plan.price_monthly != null && (
                      <span className="text-blue-500 dark:text-blue-400">€{plan.price_monthly}/{t('mo')}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Downgrade options — only shown when user already has a license */}
          {has_base_seat && downgrade_options.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{t('Downgrade license')}</p>
              <div className="flex flex-wrap gap-2">
                {downgrade_options.map((plan) => (
                  <button
                    key={plan.license_id}
                    onClick={() => handleChangePlan(plan.license_id, 'downgrade')}
                    disabled={actionLoading === plan.license_id}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    <PiIcons.PiArrowDownLight className="h-3 w-3" />
                    {plan.display_name}
                    {plan.price_monthly != null && (
                      <span className="text-gray-400 dark:text-gray-500">€{plan.price_monthly}/{t('mo')}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════ ADD-ONS TAB ═══════ */}
      {activeTab === 'addons' && (
        <div className="space-y-4">
          {/* No-seat notice: add-ons require a base seat first */}
          {!has_base_seat && !is_org_admin && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700 text-xs text-amber-700 dark:text-amber-300">
              <PiIcons.PiWarningLight className="h-4 w-4 flex-shrink-0" />
              <div>
                <p className="font-medium mb-1">{t('Seat required')}</p>
                <p>{t('This user needs an active seat before add-ons can be assigned. Go to the Plan tab to assign a seat first.')}</p>
              </div>
            </div>
          )}

          {/* Full plan notice */}
          {isFullPlan && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-700 text-xs text-blue-700 dark:text-blue-300">
              <PiIcons.PiInfoLight className="h-4 w-4 flex-shrink-0" />
              <span>{t('PONS Full includes all features. Individual add-ons are not needed.')}</span>
            </div>
          )}

          {/* Active add-ons assigned to this user (deduplicated by license_id) */}
          {user_active_addons.length > 0 && (() => {
            const seen = new Set<number>();
            const dedupedAddons = user_active_addons.filter(a => {
              if (seen.has(a.license_id)) return false;
              seen.add(a.license_id);
              return true;
            });
            return (
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                {t('Assigned to this user')}
                <span className="ml-1.5 text-gray-400 dark:text-gray-500">({dedupedAddons.length})</span>
              </p>
              <div className="space-y-2">
                {dedupedAddons.map((addon) => {
                  const isLoading = actionLoading === addon.org_license_id;
                  return (
                    <div
                      key={addon.org_license_id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-700"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <PiIcons.PiPuzzlePieceLight className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                        <span className="text-sm text-gray-900 dark:text-white truncate">
                          {addon.display_name || addon.name}
                        </span>
                        {is_org_admin && (
                          <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                            ({t('admin has all')})
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {addon.price_monthly != null && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            €{addon.price_monthly}/{addon.is_per_seat ? `${t('seat')}/` : ''}{t('mo')}
                          </span>
                        )}
                        <Button
                          variant="outline"
                          size="small"
                          onClick={() => handleRevoke(addon.org_license_id)}
                          disabled={isLoading}
                          isLoading={isLoading}
                          className="text-red-600 dark:text-red-400 border-red-200 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          {t('Revoke')}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })()}

          {/* Available add-ons (hidden for Full plan, deduplicated) */}
          {!isFullPlan && available_addons.length > 0 && (() => {
            const seenAvail = new Set<number>();
            const dedupedAvailable = available_addons.filter(a => {
              if (seenAvail.has(a.license_id)) return false;
              seenAvail.add(a.license_id);
              return true;
            });
            if (dedupedAvailable.length === 0) return null;
            return (
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                {t('Available to assign')}
                <span className="ml-1.5 text-gray-400 dark:text-gray-500">({dedupedAvailable.length})</span>
              </p>
              <div className="space-y-2">
                {dedupedAvailable.map((addon) => {
                  const key = addon.source === 'org' ? `org-${addon.org_license_id}` : `cat-${addon.license_id}`;
                  const isLoading = addon.source === 'org'
                    ? actionLoading === addon.org_license_id
                    : actionLoading === addon.license_id;
                  return (
                    <div key={key} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                      <div className="flex items-center gap-2 min-w-0">
                        <PiIcons.PiPuzzlePieceLight className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                        <span className="text-sm text-gray-900 dark:text-white truncate">
                          {addon.display_name || addon.name}
                        </span>
                        {addon.source === 'catalog' && (
                          <Badge color="gray" className="text-xs flex-shrink-0">{t('Not purchased')}</Badge>
                        )}
                        {addon.source === 'org' && addon.seat_count != null && (
                          <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                            {addon.assigned_count ?? 0}/{addon.seat_count || addon.quantity} {t('seats')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {addon.price_monthly != null && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            €{addon.price_monthly}/{addon.is_per_seat ? `${t('seat')}/` : ''}{t('mo')}
                          </span>
                        )}
                        <Button
                          variant="primary"
                          size="small"
                          onClick={() =>
                            addon.source === 'org' && addon.org_license_id
                              ? handleAssign(addon.org_license_id)
                              : handleAssignCatalog(addon.license_id)
                          }
                          disabled={isLoading || (!has_base_seat && !is_org_admin)}
                          isLoading={isLoading}
                          title={!has_base_seat && !is_org_admin ? t('User needs a seat first') : undefined}
                        >
                          {t('Assign')}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })()}

          {/* Empty states */}
          {!isFullPlan && user_active_addons.length === 0 && available_addons.length === 0 && (
            <p className="text-sm text-gray-400 dark:text-gray-500 italic py-4 text-center">
              {t('No add-ons available.')}
            </p>
          )}
          {isFullPlan && user_active_addons.length === 0 && (
            <p className="text-sm text-gray-400 dark:text-gray-500 italic py-4 text-center">
              {t('No individual add-ons — all features are included in the Full plan.')}
            </p>
          )}
        </div>
      )}

      {/* ═══════ REQUESTS TAB ═══════ */}
      {activeTab === 'requests' && (
        <div className="space-y-3">
          {requestsLoading ? (
            <div className="space-y-2 animate-fadePulse">
              <div className="h-12 bg-gray-100 dark:bg-gray-700 rounded-lg" />
              <div className="h-12 bg-gray-100 dark:bg-gray-700 rounded-lg" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-6">
              <PiIcons.PiEnvelopeSimpleLight className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-400 dark:text-gray-500">
                {t('No feature requests from this user.')}
              </p>
            </div>
          ) : (
            <>
              {/* Pending requests first */}
              {pendingRequests.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-2">
                    {t('Pending')} ({pendingRequests.length})
                  </p>
                  <div className="space-y-2">
                    {pendingRequests.map((req) => {
                      const isLoading = requestActionLoading === req.id;
                      return (
                        <div key={req.id} className="p-3 rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0">
                              <PiIcons.PiClockLight className="h-4 w-4 text-amber-500 flex-shrink-0" />
                              <span className="text-sm font-medium text-gray-900 dark:text-white">{req.section_display}</span>
                              {req.created_at && (
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                  {new Date(req.created_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Button
                                variant="primary"
                                size="small"
                                onClick={() => handleApproveRequest(req.id)}
                                disabled={isLoading}
                                isLoading={isLoading}
                              >
                                {t('Approve')}
                              </Button>
                              <Button
                                variant="outline"
                                size="small"
                                onClick={() => handleDenyRequest(req.id)}
                                disabled={isLoading}
                                className="text-red-600 dark:text-red-400 border-red-200 dark:border-red-700"
                              >
                                {t('Deny')}
                              </Button>
                            </div>
                          </div>
                          {req.message && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1.5 italic">"{req.message}"</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Resolved requests */}
              {requests.filter(r => r.status !== 'pending').length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    {t('Resolved')}
                  </p>
                  <div className="space-y-1.5">
                    {requests.filter(r => r.status !== 'pending').map((req) => (
                      <div key={req.id} className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{req.section_display}</span>
                          <Badge
                            color={req.status === 'approved' ? 'green' : 'red'}
                            className="text-xs"
                          >
                            {req.status}
                          </Badge>
                        </div>
                        {req.created_at && (
                          <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                            {new Date(req.created_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      </div>
    </div>
  );
};

interface User {
  id: number;
  uuid?: string;
  email: string;
  first_name: string;
  last_name: string;
  role?: string;
  is_org_admin: boolean;
  is_active: boolean;
  created_at: string;
}

interface UserPermission {
  id: number;
  name: string;
  description?: string;
  source: 'direct' | 'role' | 'admin';
}

interface Permission {
  id: number;
  name: string;
  description?: string;
}

interface PermissionSection {
  id: number;
  name: string;
  permissions: Permission[];
}

type PageTab = 'overview' | 'licensing' | 'permissions';

const AdminUserEdit: React.FC = () => {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);

  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<PermissionSection[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<{ value: string; label: string }[]>([]);
  const [adminCount, setAdminCount] = useState(0);
  const [orgType, setOrgType] = useState<string>('');
  const [pageTab, setPageTab] = useState<PageTab>('overview');
  const [licenseSummary, setLicenseSummary] = useState<any>(null);
  const [licenseLoaded, setLicenseLoaded] = useState(false);

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    // Get org type from userInfo
    const currentOrgType = (userInfo as any)?.organization_type || (userInfo as any)?.OrganizationType || 'law_firm';
    setOrgType(currentOrgType);

    const loadData = async () => {
      await Promise.all([fetchUser(), fetchPermissions(), fetchRoles(), fetchAdminCount()]);
      // Fetch license summary for overview
      try {
        const userIdNum = parseInt(userId);
        const licRes = await apis.get(`/admin/users/${userIdNum}/licenses`);
        if (licRes.data?.success) setLicenseSummary(licRes.data.data);
      } catch { /* non-critical */ }
      finally { setLicenseLoaded(true); }
    };
    loadData();
  }, [userId, userInfo]);

  const fetchUser = async () => {
    try {
      const response = await apis.get(`/admin/users`);
      if (response.data.success) {
        const userIdNum = parseInt(userId);
        const foundUser = response.data.data.find((u: any) => {
          return u.id === userIdNum || (u.uuid && u.uuid === userId);
        }) as User | undefined;

        if (foundUser) {
          setUser(foundUser);
          await fetchUserPermissions(foundUser.id);
        } else {
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('User not found'),
            modalType: 'error',
            duration: 4000
          }));
          history.push('/admin?tab=users');
        }
      }
    } catch (error: any) {
      console.error('Error fetching user:', error);
      dispatch(showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.error || t('Failed to fetch user'),
        modalType: 'error',
        duration: 4000
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPermissions = async (userId: number) => {
    try {
      const response = await apis.get(`/admin/users/${userId}/permissions`);
      if (response.data.success) {
        setUserPermissions(response.data.data.permissions || []);
      }
    } catch (error: any) {
      console.error('Error fetching user permissions:', error);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await apis.get('/admin/permissions');
      if (response.data.success) {
        setPermissions(response.data.data || []);
      }
    } catch (error: any) {
      console.error('Error fetching permissions:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await apis.get('/admin/roles');
      if (response.data.success) {
        const roleOptions = response.data.data.map((role: any) => ({
          value: role.name,
          label: formatRoleNameForDisplay(role.name),
        }));
        setRoles(roleOptions);
      }
    } catch (error: any) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchAdminCount = async () => {
    try {
      const response = await apis.get(`/admin/users`);
      if (response.data.success) {
        const count = response.data.data.filter((u: User) => u.is_org_admin).length;
        setAdminCount(count);
      }
    } catch (error: any) {
      console.error('Error fetching admin count:', error);
    }
  };

  const handleOpenEditModal = () => {
    if (!user) return;

    dispatch(showModal({
      showModal: true,
      type: 'adminEditUser',
      user,
      item: { adminCount },
      message: t('Edit User'),
      onConfirm: async (updatedUser: any) => {
        try {
          // Update role or admin status if changed
          if ((updatedUser.role && updatedUser.role !== user.role) || updatedUser.is_org_admin !== user.is_org_admin) {
            await apis.post(`/admin/users/${user.id}/roles`, {
              role_name: updatedUser.role,
              is_org_admin: updatedUser.is_org_admin,
            });
          }

          dispatch(showFeedbackModal({
            showModal: true,
            message: t('User updated successfully'),
            modalType: 'success',
            duration: 3000
          }));
          await fetchUser();
        } catch (error: any) {
          console.error('Error updating user:', error);
          dispatch(showFeedbackModal({
            showModal: true,
            message: error?.response?.data?.error || t('Failed to update user'),
            modalType: 'error',
            duration: 4000
          }));
        }
      }
    }));
  };

  const togglePermission = async (permissionId: number, hasPermission: boolean, source?: string) => {
    if (!user) return;

    // Role-based permissions cannot be toggled individually
    if (source === 'role') {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('This permission comes from the user\'s role. Edit the role to change it.'),
        modalType: 'warning',
        duration: 4000
      }));
      return;
    }

    try {
      if (hasPermission) {
        await apis.delete(`/admin/users/${user.id}/permissions/${permissionId}`);
        setUserPermissions(userPermissions.filter((p) => p.id !== permissionId));
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Permission revoked successfully'),
          modalType: 'success',
          duration: 2000
        }));
      } else {
        await apis.post(`/admin/users/${user.id}/permissions/${permissionId}`, {});
        const permission = permissions
          .flatMap((s) => s.permissions)
          .find((p) => p.id === permissionId);

        if (permission) {
          setUserPermissions([
            ...userPermissions,
            {
              id: permission.id,
              name: permission.name,
              description: permission.description,
              source: 'direct',
            },
          ]);
        }
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Permission granted successfully'),
          modalType: 'success',
          duration: 2000
        }));
      }
    } catch (error: any) {
      console.error('Error toggling permission:', error);
      dispatch(showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.error || t('Failed to update permission'),
        modalType: 'error',
        duration: 4000
      }));
    }
  };

  if (loading || !licenseLoaded) {
    return (
      <DetailFrame
        breadcrumbs={[
          { name: t('Admin'), href: '/admin', current: false },
          { name: t('Users'), href: '/admin?tab=users', current: false },
          { name: t('Loading...'), href: '#', current: true },
        ]}
        showBackButton={true}
      >
        <div className="flex-1 overflow-y-auto animate-fadePulse">
          {/* Header skeleton */}
          <div className="px-5 pt-5 pb-3">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
              </div>
            </div>
          </div>
          {/* Tab skeleton */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-5 pb-2 flex gap-4">
            {[1, 2, 3].map(i => <div key={i} className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-20" />)}
          </div>
          {/* Content skeleton */}
          <div className="p-5 space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="flex justify-between">
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32" />
              </div>
            ))}
          </div>
        </div>
      </DetailFrame>
    );
  }

  if (!user) {
    return (
      <DetailFrame
        breadcrumbs={[
          { name: t('Admin'), href: '/admin', current: false },
          { name: t('Users'), href: '/admin?tab=users', current: false },
          { name: t('Error'), href: '#', current: true },
        ]}
        showBackButton={true}
      >
        <div className="flex items-center justify-center h-full">
          <p className="text-red-600 dark:text-red-400">{t('User not found')}</p>
        </div>
      </DetailFrame>
    );
  }

  const getInitials = (firstName: string, lastName: string): string => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.trim();
  };

  const userPermissionIds = userPermissions.map(p => p.id);
  const visibleUserPermissions = userPermissions.filter(p => {
    const section = p.name.split(':')[0];
    return !HIDDEN_PERMISSION_SECTIONS.includes(section);
  });

  const pageTabs: { key: PageTab; label: string; icon: React.ReactNode }[] = [
    { key: 'overview', label: t('Overview'), icon: <PiIcons.PiUserLight className="h-4 w-4" /> },
    { key: 'licensing', label: t('Licensing'), icon: <PiIcons.PiShieldCheckLight className="h-4 w-4" /> },
    { key: 'permissions', label: t('Permissions'), icon: <PiIcons.PiKeyLight className="h-4 w-4" /> },
  ];

  return (
    <DetailFrame
      breadcrumbs={[
        { name: t('Admin'), href: '/admin', current: false },
        { name: t('Users'), href: '/admin?tab=users', current: false },
        { name: `${user.first_name} ${user.last_name}`, href: '#', current: true },
      ]}
      showBackButton={true}
      actions={[
        {
          label: t('Edit'),
          onClick: handleOpenEditModal,
          variant: 'primary' as const,
        },
      ]}
    >
      <div className="flex-1 overflow-y-auto custom-scrollbar-main">
        {/* ── User Header ── */}
        <div className="px-5 pt-5 pb-3">
          <div className="flex items-start gap-4">
            <ProfileImage
              profilePicture={null}
              email={user.email}
              initials={getInitials(user.first_name, user.last_name)}
              name={`${user.first_name} ${user.last_name}`}
              size="h-11 w-11"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-lg text-gray-900 dark:text-white truncate">
                  {user.first_name} {user.last_name}
                </h1>
                <Badge color={user.is_active ? 'green' : 'gray'}>
                  {user.is_active ? t('Active') : t('Inactive')}
                </Badge>
              </div>
              <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                <span>{user.email}</span>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <span>{user.is_org_admin ? t('Admin') : user.role ? formatRoleNameForDisplay(user.role) : t('Member')}</span>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <span>{t('Joined')} {formatDateTime(user.created_at)}</span>
                {licenseSummary?.base_plan && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span className="flex items-center gap-1">
                      <PiIcons.PiShieldCheckLight className="h-3 w-3" />
                      {licenseSummary.base_plan.display_name || licenseSummary.base_plan.name}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Page Tabs — full width ── */}
        <Tabs
          tabs={pageTabs.map(tab => ({ label: tab.label, value: tab.key, icon: tab.icon }))}
          activeTab={pageTab}
          onTabClick={(val) => setPageTab(val as PageTab)}
          compact
          badgeCounts={visibleUserPermissions.length > 0 ? { permissions: visibleUserPermissions.length } : undefined}
        />

      <div className='pb-4'>
        {/* ═══════ OVERVIEW TAB ═══════ */}
        {pageTab === 'overview' && (
          <div>

            {/* Details section */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 px-5 pt-3 border-b border-gray-100 dark:border-gray-700/60">
                <PiIcons.PiUserLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">{t('Details')}</span>
              </div>
              <div className="px-5 py-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                  <div>
                    <span className="text-[11px] text-gray-400 dark:text-gray-500">{t('Role')}</span>
                    <p className="text-sm text-gray-700 dark:text-gray-200 mt-0.5">{user.role ? formatRoleNameForDisplay(user.role) : '—'}</p>
                  </div>
                  <div>
                    <span className="text-[11px] text-gray-400 dark:text-gray-500">{t('Admin')}</span>
                    <p className="text-sm text-gray-700 dark:text-gray-200 mt-0.5">{user.is_org_admin ? t('Yes') : t('No')}</p>
                  </div>
                  <div>
                    <span className="text-[11px] text-gray-400 dark:text-gray-500">{t('Status')}</span>
                    <div className="mt-0.5">
                      <Badge color={user.is_active ? 'green' : 'gray'}>
                        {user.is_active ? t('Active') : t('Inactive')}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <span className="text-[11px] text-gray-400 dark:text-gray-500">{t('Joined')}</span>
                    <p className="text-sm text-gray-700 dark:text-gray-200 mt-0.5">{formatDateTime(user.created_at)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* License section */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-700/60">
                <div className="flex items-center gap-2">
                  <PiIcons.PiShieldCheckLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                  <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">{t('License')}</span>
                </div>
                <button
                  onClick={() => setPageTab('licensing')}
                  className="text-[11px] text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  {t('Manage')} →
                </button>
              </div>
              <div className="px-5 py-4">
                {licenseSummary ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                    <div>
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">{t('Plan')}</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <p className="text-sm text-gray-700 dark:text-gray-200">
                          {licenseSummary.base_plan?.display_name || licenseSummary.base_plan?.name || '—'}
                        </p>
                        {licenseSummary.base_plan?.type && (
                          <Badge color={TYPE_BADGE_COLOR[licenseSummary.base_plan.type] || 'gray'}>
                            {licenseSummary.base_plan.type.charAt(0).toUpperCase() + licenseSummary.base_plan.type.slice(1)}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">{t('Seat')}</span>
                      <div className="mt-0.5">
                        <Badge color={licenseSummary.has_base_seat ? 'green' : 'yellow'}>
                          {licenseSummary.has_base_seat ? t('Assigned') : t('No seat')}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">{t('Price')}</span>
                      <p className="text-sm text-gray-700 dark:text-gray-200 mt-0.5">
                        {licenseSummary.base_plan?.price_monthly != null ? `€${licenseSummary.base_plan.price_monthly}/${t('mo')}` : '—'}
                      </p>
                    </div>
                    <div>
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">{t('Expires')}</span>
                      <p className="text-sm text-gray-700 dark:text-gray-200 mt-0.5">
                        {licenseSummary.base_plan?.license_expires_at ? formatDateTime(licenseSummary.base_plan.license_expires_at) : '—'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 dark:text-gray-500 italic">{t('No license data available')}</p>
                )}

                {/* Active add-ons */}
                {(() => {
                  const seen = new Set<number>();
                  const dedupedAddons = (licenseSummary?.user_active_addons || []).filter((a: any) => {
                    if (seen.has(a.license_id)) return false;
                    seen.add(a.license_id);
                    return true;
                  });
                  if (dedupedAddons.length === 0) return null;
                  return (
                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/60">
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">{t('Add-ons')}</span>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {dedupedAddons.map((a: any) => (
                          <span key={a.license_id} className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700/60 px-2 py-1 rounded">
                            <PiIcons.PiPuzzlePieceLight className="h-3 w-3" />
                            {a.display_name || a.name}
                            {a.price_monthly != null && (
                              <span className="text-gray-400 dark:text-gray-500 ml-1">€{a.price_monthly}/{t('mo')}</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Permissions section */}
            {visibleUserPermissions.length > 0 && (
              <div>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-700/60">
                  <div className="flex items-center gap-2">
                    <PiIcons.PiKeyLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                    <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">{t('Permissions')}</span>
                  </div>
                  <button
                    onClick={() => setPageTab('permissions')}
                    className="text-[11px] text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  >
                    {t('View all')} →
                  </button>
                </div>
                <div className="px-5 py-4">
                  <div className="flex flex-wrap gap-1.5">
                    {(() => {
                      const sections = new Set(visibleUserPermissions.map(p => p.name.split(':')[0]));
                      return Array.from(sections).map(name => (
                        <span key={name} className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700/60 px-2 py-1 rounded">
                          {getIconByName(name)}
                          {capitalizeSection(name)}
                        </span>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════ LICENSING TAB ═══════ */}
        {pageTab === 'licensing' && (
          <UserLicensePanel userId={user.id} />
        )}

        {/* ═══════ PERMISSIONS TAB ═══════ */}
        {pageTab === 'permissions' && (
          <div className="space-y-4 p-4">
            {visibleUserPermissions.length > 0 ? (
              <>
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge color="gray" className="text-xs">
                    {t('Total')}: {visibleUserPermissions.length}
                  </Badge>
                  <span className="flex items-center gap-1 text-[10px] text-blue-600 dark:text-blue-400">
                    <PiIcons.PiLockSimpleLight className="h-3 w-3" />
                    {t('From role')}
                  </span>
                  <span className="flex items-center gap-1 text-[10px] text-green-600 dark:text-green-400">
                    ● {t('Direct')}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  {(() => {
                    const clientSpecificSections = ['lawyers', 'finder', 'finances'];
                    const nonClientOrgTypes = ['law_firm', 'solo_lawyer', 'in_house'];
                    const shouldFilter = nonClientOrgTypes.includes(orgType?.toLowerCase());

                    let filteredPermissions = permissions.filter(
                      s => !HIDDEN_PERMISSION_SECTIONS.includes(s.name.toLowerCase())
                    );
                    if (shouldFilter) {
                      filteredPermissions = filteredPermissions.filter(
                        s => !clientSpecificSections.includes(s.name.toLowerCase())
                      );
                    }

                    return filteredPermissions.map((section) => {
                      const sectionPerms = section.permissions.filter(p =>
                        userPermissionIds.includes(p.id)
                      );
                      const totalCount = section.permissions.length;
                      const selectedCount = sectionPerms.length;

                      return (
                        <div key={section.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                          <div className="px-3 py-2 text-left font-semibold text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700/30 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
                            <span className="flex items-center gap-2 flex-1 min-w-0">
                              {getIconByName(section.name)}
                              <span className="text-xs truncate">{capitalizeSection(section.name)}</span>
                            </span>
                            <Badge color="green" className="text-[10px] flex-shrink-0">
                              {selectedCount}/{totalCount}
                            </Badge>
                          </div>

                          <div className="p-2 space-y-1 max-h-48 overflow-y-auto custom-scrollbar-main">
                            {section.permissions.length === 0 ? (
                              <p className="text-xs text-gray-500 dark:text-gray-400 py-2">{t('No permissions')}</p>
                            ) : (
                              section.permissions.map((perm) => {
                                const userPerm = userPermissions.find(p => p.id === perm.id);
                                const hasPermission = !!userPerm;
                                const isRoleBased = userPerm?.source === 'role';
                                return (
                                  <button
                                    key={perm.id}
                                    onClick={() => togglePermission(perm.id, hasPermission, userPerm?.source)}
                                    className={`w-full px-2 py-1 text-xs rounded transition-colors text-left flex items-center gap-1 ${
                                      hasPermission && isRoleBased
                                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 cursor-default opacity-80'
                                        : hasPermission
                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700'
                                        : 'bg-gray-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600/50'
                                    }`}
                                    title={isRoleBased ? `${perm.description || perm.name} (from role — edit role to change)` : (perm.description || perm.name)}
                                  >
                                    <span className="truncate flex-1">{perm.description || perm.name}</span>
                                    {isRoleBased && (
                                      <PiIcons.PiLockSimpleLight className="h-3 w-3 flex-shrink-0 opacity-60" />
                                    )}
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-dashed border-gray-200 dark:border-gray-700 text-center">
                <PiIcons.PiKeyLight className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('No permissions assigned')}
                </p>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </DetailFrame>
  );
};

/* ─── PlanOptionCard — reusable row for the plan picker ──────── */

const PlanOptionCard: React.FC<{
  label: string;
  type: string;
  price: number | null;
  seats?: string;
  sections?: string[];
  isFull: boolean;
  loading: boolean;
  disabled: boolean;
  buttonLabel: string;
  highlight?: boolean;
  onClick: () => void;
  t: any;
}> = ({ label, type, price, seats, sections, isFull, loading, disabled, buttonLabel, highlight, onClick, t }) => (
  <div
    className={`rounded-lg border p-3 flex items-center justify-between ${
      highlight
        ? 'border-blue-200 dark:border-blue-700 bg-blue-50/60 dark:bg-blue-900/10'
        : isFull
        ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-60'
        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
    }`}
  >
    <div className="flex items-center gap-2.5 min-w-0">
      {highlight
        ? <PiIcons.PiCrownSimpleLight className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
        : <PiIcons.PiShieldCheckLight className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0" />
      }
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{label}</span>
          <Badge color={TYPE_BADGE_COLOR[type] || 'gray'}>{type.charAt(0).toUpperCase() + type.slice(1)}</Badge>
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          {price != null && <span>€{price}/{t('mo')}</span>}
          {seats && (
            <span className="flex items-center gap-1">
              <PiIcons.PiUsersLight className="h-3 w-3" />
              {seats} {t('seats')}
            </span>
          )}
        </div>
        {sections && sections.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {sections.slice(0, 8).map((s: string) => (
              <span key={s} className="text-[10px] text-gray-400 dark:text-gray-500 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700/50">
                {capitalizeSection(s)}
              </span>
            ))}
            {sections.length > 8 && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500">+{sections.length - 8}</span>
            )}
          </div>
        )}
      </div>
    </div>
    <Button
      variant={highlight ? 'primary' : isFull ? 'tertiary' : 'primary'}
      size="small"
      disabled={disabled}
      isLoading={loading}
      onClick={onClick}
    >
      {buttonLabel}
    </Button>
  </div>
);

export default AdminUserEdit;
